import { Flex, KpiCard, Card, Skeleton, CardProps } from '@blockanalitica/ui';
import type {
  KpiCardProps,
  KpiCardData
} from '@blockanalitica/ui/package/types/src/components/molecules/KpiCard/KpiCard';
import _ from 'lodash';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';

interface AsyncCardProps extends CardProps {
  $opacity: number;
}

export interface AsyncKpiCardProps extends Omit<KpiCardProps, 'data'> {
  data: KpiCardData | null | undefined;
  error: Error | undefined;
  isLoading: boolean;
}

const AsyncCard = styled(Card)<AsyncCardProps>`
  transition: opacity, transform;
  transition-duration: 0.15s, 0.35s;
  transition-timing-function: cubic-bezier(0.2, 0, 0, 1);
  transition-delay: 0.2s;

  ${(props) => css`
    opacity: ${props.$opacity};
    transform: ${props.$opacity === 0 ? 'translateY(30px)' : 'translateY(0)'};
  `}
`;

export default function AsyncKpiCard({
  data,
  error,
  isLoading,
  field,
  title,
  footer,
  icon,
  extra,
  options,
  ...rest
}: AsyncKpiCardProps) {
  const kpiOptions = useMemo(
    () =>
      _.merge(
        {
          currency: 'USD',
          notation: 'compact',
          variants: {
            kpiHeader: 'banner',
            valueCurrencyValue: 'normal'
          }
        },
        options
      ),
    [options]
  );

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <Flex flexDirection="column" gap="2xs">
          <Skeleton height="1" width="10" />
          <Skeleton height="9" width="250px" />
        </Flex>
      );
    }
    if (error || !data) {
      return 'Error';
    }

    return (
      <KpiCard
        variant="asyncKpiCard"
        data={data}
        field={field}
        title={title}
        footer={footer}
        icon={icon}
        extra={extra}
        options={kpiOptions}
      />
    );
  }, [error, isLoading, data, extra, field, footer, icon, kpiOptions, title]);

  return (
    <AsyncCard $opacity={isLoading ? 0 : 1} {...rest}>
      {content}
    </AsyncCard>
  );
}
